import { z } from 'zod';
import { ItemInstanceSchema } from '../data/items.js';
/**
 * Chat. A client sends text and a channel; the server decides who hears it, stamps the
 * sender from the session (never from the payload), and broadcasts.
 */
/** Short enough that a line reads whole in the bubble over the speaker's head. */
export const CHAT_MAX_LENGTH = 60;
/**
 * Collapse whitespace, then strip control and format characters.
 *
 * Zero-width and bidirectional controls are the usual tools for impersonation
 * and for breaking a chat log, so they never reach other players. Unicode
 * categories rather than a hand-kept list: Cc is every control character, Cf
 * every invisible formatting one, and the list cannot go stale.
 */
function sanitise(text) {
    return text
        .replace(/\s+/g, ' ')
        .replace(/[\p{Cc}\p{Cf}]/gu, '')
        .trim();
}
/**
 * Items linked in a line (ADR 0085): the text names each by a token, `[#1]`
 * for the first, and the message lists the ids. The server stamps a copy of
 * each piece, as the sender holds it, into what everyone receives; a click
 * on the link opens its card. Three, so a line stays a sentence.
 */
export const CHAT_MAX_LINKS = 3;
/** The token standing for the `index`-th (from 0) linked item in a line's text. */
export function chatLinkToken(index) {
    return `[#${String(index + 1)}]`;
}
/**
 * Splits `text` around the tokens of `links`. A token whose item is missing
 * (`[#4]`, or a line typed by hand) stays text: nothing a player types can
 * draw an item that the server did not stamp.
 */
export function chatSegments(text, links = []) {
    const segments = [];
    let rest = '';
    const flush = () => {
        if (rest.length > 0)
            segments.push({ text: rest });
        rest = '';
    };
    for (const part of text.split(/(\[#\d\])/)) {
        const match = /^\[#(\d)\]$/.exec(part);
        const item = match === null ? undefined : links[Number(match[1]) - 1];
        if (item === undefined) {
            rest += part;
            continue;
        }
        flush();
        segments.push({ item });
    }
    flush();
    return segments;
}
/**
 * Who hears a line (ADR 0033): everyone in the game (`general`), the zone
 * around the speaker, their party wherever its members are (ADR 0032), their
 * guild (ADR 0036), or a single player named by `to` (`private`).
 */
export const CHAT_CHANNELS = ['general', 'zone', 'party', 'guild', 'private'];
export const ChatChannelSchema = z.enum(CHAT_CHANNELS);
export const ChatMessageSchema = z
    .object({
    channel: ChatChannelSchema.default('zone'),
    text: z
        .string()
        .transform(sanitise)
        .pipe(z.string().min(1, 'Message vide').max(CHAT_MAX_LENGTH, 'Message trop long')),
    /** The addressee's name, for a private line only. */
    to: z.string().trim().min(1).max(32).optional(),
    /** Ids of the items the text links, in token order: carried or worn by the sender. */
    links: z.array(z.uuid()).min(1).max(CHAT_MAX_LINKS).optional(),
})
    .refine((message) => (message.channel === 'private') === (message.to !== undefined), {
    message: 'A private line names its addressee, and only a private line does',
    path: ['to'],
});
/** What every client in range receives. The name is the server's, not the sender's claim. */
export const ChatBroadcastSchema = z.object({
    from: z.string().min(1),
    name: z.string().min(1),
    text: z.string().min(1).max(CHAT_MAX_LENGTH),
    channel: ChatChannelSchema.default('zone'),
    /** A private line's addressee, so the sender's own copy reads « À Bob ». */
    to: z.string().min(1).optional(),
    /**
     * The speaker's aura (ADR 0013), stamped by the server like the name: the
     * name is drawn in its colour from +3, proof of the outfit in the chat.
     */
    aura: z.number().int().min(0).max(10).default(0),
    /** A game master (ADR 0025), from the account's flag: `[GM]` in violet before the name. */
    gm: z.boolean().default(false),
    /** The speaker's guild tag (ADR 0036), stamped by the server; empty without a guild. */
    guild: z.string().max(4).optional(),
    /** The linked items as the sender held them when speaking, in token order. */
    links: z.array(ItemInstanceSchema).max(CHAT_MAX_LINKS).optional(),
    /** Server time, so clients order messages identically. */
    at: z.number().int().positive(),
});
/** Sent back to a sender whose message was dropped. */
export const ChatRejectedSchema = z.object({
    /**
     * `no-party` / `no-guild`: a line for a party or a guild from someone in
     * none; `no-recipient`: nobody of that name is in the game; `muted`: a
     * sanction stands (ADR 0037); `no-item`: a linked item is no longer the
     * sender's (ADR 0085).
     */
    reason: z.enum([
        'invalid',
        'rate-limited',
        'no-party',
        'no-guild',
        'no-recipient',
        'muted',
        'no-item',
    ]),
    /** Milliseconds until the next message is accepted, when rate-limited or muted. */
    retryInMs: z.number().int().min(0).optional(),
});
//# sourceMappingURL=chat.js.map